import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { COLORS } from '../Styles/Colors';
import { EditButton, PageContainer, AddButton, BulkImportLink, ModalOverlay, ModalContent, ModalHeader, CloseButton, TrackIconWrapper } from '../Styles/AppStyles';
import { IoIosPin } from 'react-icons/io';
import { Link } from 'react-router-dom';
import apiClient from '../Services/ApiClient';
import { useSnackbar } from '../Context/SnackbarProvider';
import { Box, Typography, Button } from '@mui/material';
import {
  FormActions,
  FormCancelButton,
  FormSubmitButton,
} from '../Components/Form';
import { DownloadIcon, UploadIcon } from 'lucide-react';
import { PencilIcon } from 'lucide-react';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { AssetMergeExportDialog } from './Assets/AssetMergeExportDialog';
import { AssetEditForm, AssetFormInput } from './Assets/AssetEditForm';
import { LocationSelect } from '../Components/Select/LocationSelect';
import useGetCustomData from '../Hooks/useGetCustomData';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import apiClientFormData from '../Services/ApiClientFormData';
import { formatDateOnly, formatUTC } from '../Services/FormatUtc';
import { formatDatePayload } from '../Services/FormatDatePayload';

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;

// Define Asset interface
export interface Asset {
  OVID?: number | null;
  id: number;
  name: string;
  serial_number?: string;
  is_active: boolean;
  checked_out: boolean;
  needs_repair: boolean;
  needs_service: boolean;
  service_interval_months?: number;
  last_service_date?: string;
  service_due_date?: string;
  device_id?: any;
  gps_device_id?: string | null;
  part_number: string;
  category_id?: number;
  category?: string | null;
  barcode?: string;
  manufacturer?: string | null;
  manufacturer_id?: string | null;
  bin_location_id?: number | null;
  cost?: number | null;
  invoice_number?: string | null;
}

// Styled components — bulk import only
const ImportStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 8px;
  background-color: #f8f9fa;
`;

const ImportStepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitle = styled.span`
  color: ${COLORS.darkGray};
  font-size: 1rem;
  font-weight: 600;
`;

const ImportInstructionText = styled.p`
  margin: 0;
  color: ${COLORS.darkGray};
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: center;
`;

const ColumnList = styled.ul`
  margin: 0;
  padding: 12px 16px 12px 32px;
  background-color: ${COLORS.white};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.orange};
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  line-height: 1.8;
`;

const TemplateDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-2px);
  }
`;

const FileDropZone = styled.label<{ $hasFile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 20px;
  border: 2px dashed ${({ $hasFile }) => ($hasFile ? COLORS.teal : COLORS.gray)};
  border-radius: 8px;
  background-color: ${COLORS.white};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.teal};
    background-color: ${COLORS.teal}08;
  }

  input {
    display: none;
  }
`;

const SelectedFileName = styled.span`
  color: ${COLORS.teal};
  font-size: 0.9rem;
  font-weight: 600;
  word-break: break-all;
  text-align: center;
`;

const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImportContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 560px;
`;

const ImageUploadText = styled.span`
  color: ${COLORS.darkGray};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const ImageUploadSubtext = styled.span`
  color: ${COLORS.lightGray};
  font-size: 0.75rem;
  text-align: center;
`;



// BulkImportComponent
const BulkImportComponent: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient('/api/assets/download-template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Asset-Import-Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleImport = async () => {
    if (!file) {
      showSnackbar('Please select a file', 'error');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClientFormData('/api/assets/import', { method: 'POST', body: formData });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import assets');
      }
      const importedAssets: Asset[] = await response.json();
      showSnackbar(`Successfully imported ${importedAssets.length} asset(s)`, 'success');
      onSuccess();
      onClose();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to import assets', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <ImportWrapper>
      <ImportContent>
        <ImportInstructionText>
          Import multiple assets at once using an Excel spreadsheet.
          Download the template first to ensure your file has the correct column format.
        </ImportInstructionText>

        <ColumnList>
          <li><strong>Description</strong> — asset name (required)</li>
          <li><strong>Part No.</strong> — SKU or part number</li>
          <li><strong>Serial No.</strong> — unique identifier (required)</li>
          <li><strong>Location</strong> — warehouse or job site (OVID-Name format)</li>
          <li><strong>Category</strong> — must match an existing category</li>
          <li><strong>Last Service Date</strong> — optional (MM/DD/YYYY)</li>
          <li><strong>Service Due Date</strong> — optional (MM/DD/YYYY)</li>
          <li><strong>Bin Location</strong> — optional storage location</li>
          <li><strong>Cost</strong> — optional unit cost</li>
          <li><strong>Invoice</strong> — optional invoice number</li>
        </ColumnList>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>1</StepNumber>
            <StepTitle>Download the template</StepTitle>
          </ImportStepHeader>
          <TemplateDownloadButton type="button" onClick={handleDownloadTemplate}>
            <DownloadIcon size={18} />
            Download Excel Template
          </TemplateDownloadButton>
        </ImportStep>

        <ImportStep>
          <ImportStepHeader>
            <StepNumber>2</StepNumber>
            <StepTitle>Upload your completed file</StepTitle>
          </ImportStepHeader>
          <FileDropZone $hasFile={!!file}>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <UploadIcon size={28} color={COLORS.teal} />
            {file ? (
              <SelectedFileName>{file.name}</SelectedFileName>
            ) : (
              <>
                <ImageUploadText>Click to select an .xlsx file</ImageUploadText>
                <ImageUploadSubtext>Only Excel (.xlsx) files are accepted</ImageUploadSubtext>
              </>
            )}
          </FileDropZone>
        </ImportStep>

        <FormActions onCancel={onClose}>
          <FormCancelButton type="button" onClick={onClose}>Cancel</FormCancelButton>
          <FormSubmitButton type="button" onClick={handleImport} disabled={!file || importing}>
            {importing ? 'Importing...' : 'Import Assets'}
          </FormSubmitButton>
        </FormActions>
      </ImportContent>
    </ImportWrapper>
  );
};




const AssetsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [mergeExportOpen, setMergeExportOpen] = useState(false);

  const [assetToEdit, setAssetToEdit] = useState<Asset | null>(null);
  const [editedCells, setEditedCells] = useState<Record<string, Record<string, any>>>({});
  const [activeEdit, setActiveEdit] = useState<{id: string, field: string} | null>(null);

  const {showSnackbar} = useSnackbar();
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState<string>('part_number');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(field || 'part_number');
    setSortOrder(order ?? 'asc');
    setPage(0);
  };

  const searchFields = ['CustomerName', 'location_name', 'name', 'serial_number', 'part_number', 'barcode', 'manufacturer', 'bin_location_name', 'category'];
  const { columnSearches, setSearch, clearSearch } = useColumnSearches(searchFields);

  const handleColumnSearch = (field: string, val: string) => {
    setSearch(field, val);
    setPage(0);
  };

  const handleClearColumnSearch = (field: string) => {
    clearSearch(field);
    setPage(0);
  };

  const paginatedUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedOvs) params.set('ovid', selectedOvs);
    if (sortField) {
      params.set('sort_field', sortField);
      params.set('sort_order', sortOrder);
    }
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    params.set('limit', String(pageSize));
    params.set('offset', String(page * pageSize));
    return `/api/assets?${params.toString()}`;
  }, [selectedOvs, sortField, sortOrder, columnSearches, page, pageSize]);
  const [assets, loadingAssets, errorAssets, refetchAssets] = useGetCustomData(paginatedUrl);
  const totalRows = assets?.totalCount ?? 0;
  const assetsLoading = loadingAssets || !assets?.data;

  const handleLocationChange = useCallback((val: string) => {
    setSelectedOvs(val);
    setPage(0);
  }, []);

  
 



 const handleSubmit = async (formData: AssetFormInput) => {
  const isNew = !assetToEdit;


  const assetPayload: any = {
    name: formData.name,
    serial_number: formData.serial_number || null,
    ovid: formData.ovid || null,
    barcode: formData.barcode || null,
    service_interval_months: formData.service_interval_months || null,
    last_service_date: formatDatePayload(formData.last_service_date ?? null),
    service_due_date: formatDatePayload(formData.service_due_date || null),
    category: formData.category || null,
    category_id: formData.category_id || null,
    manufacturer: formData.manufacturer || null,
    manufacturer_id: formData.manufacturer_id || null,
    part_number: formData.part_number || null,
    device_id: formData.device_id || null,
    bin_location_id: formData.bin_location_id || null,
    is_active: formData.is_active !== undefined ? formData.is_active : true,
    checked_out: isNew ? false : assetToEdit.checked_out,
    needs_repair: isNew ? false : assetToEdit.needs_repair,
    needs_service: isNew ? false : assetToEdit.needs_service,
    cost: formData.cost ?? null,
    invoice_number: formData.invoice_number ?? null,
  };

  const url = isNew ? '/api/assets' : `/api/assets/${(assetToEdit as Asset).id}`;
  const method = isNew ? 'POST' : 'PUT';

  const form = new FormData();
  form.append('data', JSON.stringify(assetPayload));
  if (formData.image) {
    form.append('image', formData.image);
  }

  try {
    const response = await apiClientFormData(url, {
      method,
      body: form,
    });

    if (!response.ok) {
      const errorData = await response.json();
      showSnackbar(errorData.error || 'Failed to add asset', 'error', 'Something went wrong!');
      return;
    }

    const newAsset: Asset = await response.json();

    if(isNew) {
    showSnackbar(`${newAsset.name} is successfully added.`, 'success', 'Successfully Added!');
    } else {
      showSnackbar(`${newAsset.name} is successfully updated.`, 'success', 'Successfully Updated!');
    }

    refetchAssets();
    handleCloseModal();
  } catch (err: any) {
    setError(err.message);
    showSnackbar(err.message, 'error', 'Something went wrong!');
  }
};



  // Inline editing handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeEdit) {
        setActiveEdit(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeEdit]);

  const handleCellChange = (id: string, field: string, value: any) => {
    setEditedCells(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value
      }
    }));
  };

  const handleCancelChanges = () => {
    setEditedCells({});
    setActiveEdit(null);
  };

  const handleSaveChanges = async () => {
    try {
      const updatePromises = [];

      for (const [id, fields] of Object.entries(editedCells)) {
        const original = assets.data?.find((item: any) => item.id === Number(id));
        if (!original) continue;

        const updateData: any = {};
        if (fields.cost !== undefined) {
          updateData.cost = fields.cost === '' || fields.cost === null ? null : Number(fields.cost);
        }
        if (fields.invoice_number !== undefined) {
          updateData.invoice_number = fields.invoice_number === '' ? null : fields.invoice_number;
        }

        if (Object.keys(updateData).length > 0) {
          const formData = new FormData();
          formData.append('data', JSON.stringify(updateData));
          
          updatePromises.push(
            apiClientFormData(`/api/assets/${id}`, {
              method: 'PUT',
              body: formData,
            })
          );
        }
      }

      await Promise.all(updatePromises);
      showSnackbar('Successfully updated asset cost and invoice information', 'success', 'Successfully Updated!');
      setEditedCells({});
      setActiveEdit(null);
      refetchAssets();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to save changes', 'error', 'Something went wrong!');
    }
  };

  const renderEditableCell = (params: any, field: string) => {
    const isEdited = editedCells[String(params.id)]?.[field] !== undefined;
    const isActive = activeEdit?.id === String(params.id) && activeEdit?.field === field;
    const displayValue = isEdited ? editedCells[String(params.id)][field] : (params.value ?? '');

    const handleCellClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setActiveEdit({ id: String(params.id), field });
    };

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: isEdited ? `${COLORS.teal}15` : 'transparent',
          position: 'relative',
          cursor: 'pointer',
        }}
        onClick={handleCellClick}
      >
        {isActive ? (
          <input
            type={field === 'cost' ? 'number' : 'text'}
            step={field === 'cost' ? '0.01' : undefined}
            value={displayValue}
            onChange={(e) => handleCellChange(
              String(params.id),
              field,
              field === 'cost' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value
            )}
            onBlur={() => setActiveEdit(null)}
            autoFocus
            style={{
              width: '100%',
              border: `1px solid ${COLORS.teal}`,
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '14px',
            }}
          />
        ) : (
          <span style={{ width: '100%' }}>
            {field === 'cost' && displayValue !== null && displayValue !== ''
              ? `${Number(displayValue).toFixed(2)}`
              : displayValue || '-'}
          </span>
        )}
      </div>
    );
  };

   const columnsNew: ColumnDef<any>[] = [
    { field: 'CustomerName', headerName: 'Account', width: 160, sortable: true, defaultFirst: true, renderHeader: () => <SearchableHeader field="CustomerName" headerName="Account" searchValue={columnSearches.CustomerName} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    { field: 'location_name', headerName: 'Location', width: 211, sortable: true, renderHeader: () => <SearchableHeader field="location_name" headerName="Location" searchValue={columnSearches.location_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    { field: 'name', headerName: 'Description', width: 334, sortable: true, filterable: true, renderHeader: () => <SearchableHeader field="name" headerName="Description" searchValue={columnSearches.name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    { field: 'serial_number', headerName: 'Serial No.', width: 138, sortable: true, filterable: true, renderHeader: () => <SearchableHeader field="serial_number" headerName="Serial No." searchValue={columnSearches.serial_number} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    { field: 'part_number', headerName: 'Part No.', width: 137, sortable: true, filterable: true, renderHeader: () => <SearchableHeader field="part_number" headerName="Part No." searchValue={columnSearches.part_number} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    { field: 'barcode', headerName: 'Barcode', width: 125, sortable: true, renderHeader: () => <SearchableHeader field="barcode" headerName="Barcode" searchValue={columnSearches.barcode} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    { field: 'created_at', headerName: 'Date Created', width: 115, sortable: true, filterable: true, renderCell: (params) => formatDateOnly(params.row.created_at) },

    { field: 'manufacturer', headerName: 'Manufacturer', width: 130, sortable: true, renderHeader: () => <SearchableHeader field="manufacturer" headerName="Manufacturer" searchValue={columnSearches.manufacturer} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },

    {
      field: 'bin_location_name',
      headerName: 'Bin Name',
      width: 120,
      sortable: true,
      renderHeader: () => <SearchableHeader field="bin_location_name" headerName="Bin Name" searchValue={columnSearches.bin_location_name} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} />,
    },

    { field: 'category', headerName: 'Category', width: 120, sortable: true, renderHeader: () => <SearchableHeader field="category" headerName="Category" searchValue={columnSearches.category} onSearchChange={handleColumnSearch} onClearSearch={handleClearColumnSearch} /> },
    

    { field: 'is_active', headerName: 'Active', width: 80, type: 'boolean' ,renderCell: (params) => params.value ? 'Y' : 'N'  },
    { field: 'checked_out', headerName: 'Checked Out', width: 120, type: 'boolean' ,renderCell: (params) => params.value ? 'Y' : 'N'  },

    { field: 'last_service_date', headerName: 'Last Service Date', width: 130, sortable: true },
    { field: 'service_due_date', headerName: 'Service Due Date', width: 130, sortable: true, renderCell: (params) => formatUTC(params.row.service_due_date) },
    { field: 'service_interval_months', headerName: 'Service Interval', width: 130, sortable: true },

    { field: 'needs_service', headerName: 'Needs Service', width: 120, type: 'boolean' ,renderCell: (params) => params.value ? 'Y' : 'N'  },
    { field: 'needs_repair', headerName: 'Needs Repair', width: 120, type: 'boolean' ,renderCell: (params) => params.value ? 'Y' : 'N'  },
    { 
      field: 'cost', 
      headerName: 'Cost', 
      width: 120, 
      sortable: true,
      renderCell: (params) => renderEditableCell(params, 'cost')
    },
    { 
      field: 'invoice_number', 
      headerName: 'Invoice', 
      width: 150, 
      sortable: true,
      renderCell: (params) => renderEditableCell(params, 'invoice_number')
    },
   {
  field: 'track',
  headerName: 'Track',
  width: 100,
  sortable: false,
  filterable: false,
  renderCell: (params) => {
    const deviceId: any = params.row.gps_device_id

    if (!deviceId) {
      return null;
    }


    return (
      <Link
        to={`/maps?deviceId=${deviceId}`}
        title="View GPS Tracking"
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <TrackIconWrapper>
          <TypedIoIosPin />
        </TrackIconWrapper>
      </Link>
    );
  },
},
  {
    field: 'count',
    headerName: 'Count',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const partNumber = params.row.part_number;
      if (!partNumber || !assets.data) return 0;
      return assets.data.filter((asset: Asset) => asset.part_number === partNumber).length;
    }
  },
  {
    field: 'actions',
    headerName: '',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: "center", marginTop: 12 }}>
        <EditButton onClick={() => {
          setAssetToEdit(params.row);
          setShowAddModal(true);
        }}>
          <PencilIcon />
        </EditButton>
      </div>
    )
  },
  ];


  const handleCloseModal = () => {
    setShowAddModal(false)
    setAssetToEdit(null);
  }

  return (
    <PageContainer>

      <Header title='View Assets' count={totalRows} hideCount={false}>
           <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
           <BulkImportLink onClick={() => setShowImportModal(true)}>Bulk Import</BulkImportLink>
      </Header>


      {showAddModal && (


        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()} >
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            <ModalHeader>{assetToEdit ? "Edit" : "Add"} Asset</ModalHeader>
            <AssetEditForm onSubmit={handleSubmit} onCancel={handleCloseModal} defaultValues={assetToEdit}/>
          </ModalContent>
        </ModalOverlay>
      )}

      {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <BulkImportComponent
              onClose={() => setShowImportModal(false)}
              onSuccess={() => refetchAssets()}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {Object.keys(editedCells).length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {Object.keys(editedCells).length} asset(s) have unsaved changes
          </Typography>
          <Button
            variant="outlined"
            onClick={handleCancelChanges}
            sx={{ color: COLORS.teal, borderColor: COLORS.teal }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            sx={{ backgroundColor: COLORS.teal, color: 'white' }}
          >
            Save Changes
          </Button>
        </Box>
      )}
      <DataTable
        data={assets.data ?? []}
        columns={columnsNew}
        pageKey="assets-v2"
        defaultColumnVisibility={{ CustomerName: false }}
        loading={assetsLoading}
        initialPageSize={pageSize}
        idField="id"
        serverSidePagination={true}
        page={page}
        rowCount={assets.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        defaultSort={[{ field: 'part_number', sort: 'asc' }]}
        onSortModelChange={handleSortChange}
        onMergeExport={() => setMergeExportOpen(true)}
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={handleLocationChange}
          setDefault={true}
          defaultName="All Locations"
        />
      </DataTable>

      <AssetMergeExportDialog
        open={mergeExportOpen}
        onClose={() => setMergeExportOpen(false)}
        initialFilters={{ selectedOvs, columnSearches }}
      />

    </PageContainer>
  );
};


export default AssetsPage;